import { useState } from 'react';
import { Activity, TriangleAlert } from 'lucide-react';
import { Button, Chip, Tooltip } from '@nyce/admin-kit/ui';
import { db, type DbDatabase, type DbServerRef, type Job } from '../../api/db';
import {
  EmptyState,
  ErrorBox,
  Kpi,
  Loading,
  SortSelect,
  SortTh,
  TableScroller,
  useAsync,
  useSort,
  type SortKeys,
} from '../../components/common';
import { useParam } from '../statistics/shared';
import { elapsedMs, formatBytes, formatCount, formatDateTime, formatDuration } from '../../lib/format';
import { Bar, ConfirmDialog, loadOverview, ProtectedChip, startDownload, type DbScopes } from './shared';
import { DbMetricsDialog } from './DbMetrics';

/** Past this transaction-id age a database is getting close to wraparound (autovacuum_freeze_max_age is 200 M). */
export const XID_WARN = 1.5e9;

const DATABASE_SORT: SortKeys<DbDatabase> = {
  name: { label: 'Database', value: (d) => d.name },
  size: { label: 'Size', value: (d) => d.sizeBytes, desc: true },
  connections: { label: 'Connections', value: (d) => d.connections, desc: true },
  xid: { label: 'XID age', value: (d) => d.xidAge, desc: true },
  environments: { label: 'Environments', value: (d) => d.environmentIds.join(', ') },
};

type Pending = { kind: 'backup' | 'live'; db: string } | null;

export function OverviewTab({
  server,
  scopes,
  onTables,
  onJob,
  notify,
}: {
  server: DbServerRef;
  scopes: DbScopes;
  onTables: (db: string) => void;
  onJob: (job: Job) => void;
  notify: (text: string) => void;
}) {
  const ov = useAsync(() => loadOverview(server.id, true), [server.id]);
  const [pending, setPending] = useState<Pending>(null);
  const [metrics, setMetrics] = useState(false);
  const sort = useSort(ov.data?.databases, DATABASE_SORT, useParam('sort', ''));
  const o = ov.data;
  const c = o?.connections;
  const usedPct = o && c ? c.total / Math.max(1, o.maxConnections) : 0;

  const backup = async (name: string) => {
    const job = await db.jobs.backup(server.id, name);
    setPending(null);
    onJob(job);
  };
  const live = async (name: string) => {
    const r = await db.liveDownload(server.id, name);
    setPending(null);
    startDownload(r.url);
    notify(`Download of ${name} started — the browser receives the dump while pg_dump runs.`);
  };

  return (
    <>
      {server.instance && (
        <div className="dbx-toolbar">
          <Button size="sm" variant="ghost" icon={<Activity size={14} />} onClick={() => setMetrics(true)}>
            CPU &amp; memory
          </Button>
        </div>
      )}
      {metrics && server.instance && (
        <DbMetricsDialog
          instance={server.instance}
          name={server.name || server.id}
          onClose={() => setMetrics(false)}
        />
      )}
      <ErrorBox error={ov.error} />
      <Loading loading={ov.loading && !o} />
      {o && c && (
        <>
          <div className="nyk-kpis dbx-kpis">
            <Kpi
              label="Version"
              value={<span className="dbx-kpi-text">{shortVersion(o.version)}</span>}
              help={o.version}
            />
            <Kpi
              label="Uptime"
              value={formatDuration(elapsedMs(o.startedAt))}
              sub={`since ${formatDateTime(o.startedAt)}`}
            />
            <Kpi
              label="Connections"
              value={`${c.total} / ${o.maxConnections}`}
              sub={
                <Bar
                  parts={[{ value: c.total }]}
                  max={o.maxConnections}
                  label={`${Math.round(usedPct * 100)}% of max_connections`}
                  tone={usedPct >= 0.9 ? 'bad' : usedPct >= 0.75 ? 'warn' : undefined}
                />
              }
              help="Client backends in use versus max_connections (superuser-reserved slots included)."
            />
            <Kpi
              label="Sessions"
              value={
                <span className="dbx-kpi-text" data-testid="session-states">
                  {c.active} active · {c.idle} idle
                </span>
              }
              sub={
                <span className={c.idleInTransaction ? 'dbx-warn-text' : undefined}>
                  {c.idleInTransaction} idle in transaction
                </span>
              }
              help="Idle-in-transaction sessions hold locks and block vacuum; a few that stay for minutes are worth a look."
            />
          </div>
          {!o.databases.length ? (
            <EmptyState card title="No user databases on this server" />
          ) : (
            <div className="nyk-card">
              <SortSelect sort={sort} />
              <TableScroller>
                <table className="nyk-ui-table nyk-cards" data-testid="db-databases">
                  <thead>
                    <tr>
                      <SortTh sort={sort} by="name" />
                      <SortTh sort={sort} by="size" className="nyk-ui-num" />
                      <SortTh sort={sort} by="connections" className="nyk-ui-num" />
                      <SortTh sort={sort} by="xid" className="nyk-ui-num" />
                      <SortTh sort={sort} by="environments" />
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {sort.rows.map((d) => (
                      <DatabaseRow
                        key={d.name}
                        d={d}
                        scopes={scopes}
                        onTables={() => onTables(d.name)}
                        onBackup={() => setPending({ kind: 'backup', db: d.name })}
                        onLive={() => setPending({ kind: 'live', db: d.name })}
                      />
                    ))}
                  </tbody>
                </table>
              </TableScroller>
            </div>
          )}
        </>
      )}
      {pending?.kind === 'backup' && (
        <ConfirmDialog
          title={`Back up ${pending.db}`}
          confirmLabel="Start backup"
          onClose={() => setPending(null)}
          onConfirm={() => backup(pending.db)}
        >
          <p>
            Runs <code>pg_dump -Fc</code> of <b>{pending.db}</b> on <b>{server.name || server.id}</b> as a
            background job and stores the dump in the backup bucket. You can follow it on the Jobs tab.
          </p>
          {server.protected && (
            <p className="dbx-muted">
              <ProtectedChip /> The server is protected: a backup only reads from it.
            </p>
          )}
        </ConfirmDialog>
      )}
      {pending?.kind === 'live' && (
        <ConfirmDialog
          title={`Live download of ${pending.db}`}
          confirmLabel="Download"
          onClose={() => setPending(null)}
          onConfirm={() => live(pending.db)}
        >
          <p>
            Streams <code>pg_dump -Fc</code> of <b>{pending.db}</b> straight to your browser — nothing is
            stored in the bucket. Large databases take as long as the dump itself; keep the tab open until the
            file is complete.
          </p>
          <p className="dbx-muted">The link is single-use and expires after 2 minutes.</p>
        </ConfirmDialog>
      )}
    </>
  );
}

function DatabaseRow({
  d,
  scopes,
  onTables,
  onBackup,
  onLive,
}: {
  d: DbDatabase;
  scopes: DbScopes;
  onTables: () => void;
  onBackup: () => void;
  onLive: () => void;
}) {
  const xidWarn = d.xidAge > XID_WARN;
  return (
    <tr data-row={d.name}>
      <td className="nyk-card-title">
        <b>{d.name}</b>
      </td>
      <td className="nyk-ui-num" data-label="Size">
        {formatBytes(d.sizeBytes)}
      </td>
      <td className="nyk-ui-num" data-label="Connections">
        {d.connections}
      </td>
      <td className="nyk-ui-num" data-label="XID age">
        {xidWarn ? (
          <Tooltip text="Close to transaction-id wraparound: check that (auto)vacuum can freeze this database.">
            <span className="dbx-warn-text" data-xid-warn>
              <TriangleAlert size={12} aria-hidden="true" /> {formatCount(d.xidAge)}
            </span>
          </Tooltip>
        ) : (
          formatCount(d.xidAge)
        )}
      </td>
      <td data-label="Environments">
        <span className="nyk-inline">
          {d.environmentIds.length ? (
            d.environmentIds.map((e) => (
              <Chip key={e} size="sm">
                {e}
              </Chip>
            ))
          ) : (
            <span className="dbx-muted">—</span>
          )}
        </span>
      </td>
      <td className="nyk-row-actions">
        <Button size="sm" variant="ghost" onClick={onTables}>
          Tables
        </Button>
        {scopes.backup && (
          <>
            <Button size="sm" variant="ghost" onClick={onBackup}>
              Backup now
            </Button>
            <Tooltip text="Streams pg_dump directly to your browser; nothing is stored.">
              <Button size="sm" variant="ghost" onClick={onLive}>
                Live download
              </Button>
            </Tooltip>
          </>
        )}
      </td>
    </tr>
  );
}

/** `PostgreSQL 16.4 on x86_64-pc-linux-gnu, compiled by …` → `PostgreSQL 16.4`. */
export const shortVersion = (v: string) => /^(\S+\s+[\d.]+)/.exec(v)?.[1] ?? v;
